'use client'

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {
  useForm,
  FieldErrors,
  UseFormReturn,
  type Resolver,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z, ZodSchema } from 'zod'
import { cx } from 'class-variance-authority'

import type { Field } from '@skop-app/lib-domain/types'
import { Button } from '@/components/atoms/Button/button.atom'
import { ScrollArea } from '@/components/atoms/ScrollArea/scrollArea.atom'
import { Tabs } from '@/components/atoms/Tabs/tabs.atom'
import { Form } from '@/components/molecules/Form/form.molecule'
import { DynamicFormFieldArray } from './DynamicFormFieldArray'
import { DynamicFormFieldRender } from './DynamicFormFieldRender'
import { getValueAtPath } from './dynamicForm.helpers'

type DynamicFormVariants = string

type DynamicFormValues = Record<string, unknown>

export type DynamicFormSection = {
  id: string
  title?: string | React.ReactNode
  tabLabel?: string | React.ReactNode
  fields: Field[]
}

export type DynamicFormSectionsVariant = 'stack' | 'tabs'

type DynamicFormChildrenProps = {
  /**
   * Compatibility helper for custom buttons.
   * - If called with callbacks, delegates to `react-hook-form` `handleSubmit`.
   * - If used directly as an event handler (e.g. `onClick={handleSubmit}`), it
   *   triggers the form submit handlers provided to `DynamicForm`.
   */
  handleSubmit: UseFormReturn<DynamicFormValues>['handleSubmit'] & ((
    e?: React.BaseSyntheticEvent,
  ) => void)
  form: UseFormReturn<DynamicFormValues>
}

type DynamicFormProps = {
  id?: string
  fields?: Field[]
  sections?: DynamicFormSection[]
  values: DynamicFormValues
  onSubmit: (data: DynamicFormValues) => void
  onSubmitError?: (errors: FieldErrors<DynamicFormValues>) => void
  children?: (props: DynamicFormChildrenProps) => React.ReactNode
  formInstance?: React.MutableRefObject<any>
  /** @deprecated `values` is reactive; this prop is ignored and will be removed. */
  syncValues?: boolean
  className?: string
  variant?: DynamicFormVariants
  locale?: string
  maxHeight?: string | number
  sectionsVariant?: DynamicFormSectionsVariant
  defaultSectionId?: string
}

const DynamicForm = forwardRef(
  (
    {
      id,
      fields,
      sections,
      values,
      onSubmit,
      onSubmitError,
      children,
      className,
      variant,
      locale,
      maxHeight,
      syncValues: _syncValues,
      sectionsVariant = 'stack',
      defaultSectionId,
      formInstance,
    }: DynamicFormProps,
    ref,
  ) => {
    const [showOptions, setShowOptions] = useState(false)
    const hasSections = (sections?.length ?? 0) > 0
    const flatFields = useMemo(
      () => (hasSections ? sections.flatMap((section) => section.fields) : (fields ?? [])),
      [hasSections, sections, fields],
    )

    useEffect(() => {
      const localeFactory = z.locales[locale || 'fr'] ?? z.locales.fr
      z.config(localeFactory())
    }, [locale])

    const schema = useMemo(
      () =>
        z.object(
          flatFields
            .filter((f) => f.type !== 'title')
            .reduce((acc, field) => {
              if (Array.isArray(field)) {
                field.forEach((subField) => {
                  acc[subField.name] = subField.validation
                })
              } else {
                if (field.type === 'stepper' && !field.validation) {
                  acc[field.name] = z.number().min(0).prefault(0)
                } else {
                  acc[field.name] = field.validation
                }
              }
              return acc
            }, {}),
        ) as ZodSchema<any>,
      [flatFields],
    )

    const form = useForm<DynamicFormValues>({
      resolver: zodResolver(schema) as Resolver<DynamicFormValues>,
      mode: 'onSubmit',
      reValidateMode: 'onBlur',
      values,
    })

    const handleSubmit = form.handleSubmit(onSubmit, onSubmitError)

    const childHandleSubmit = ((...args: any[]) => {
      if (typeof args[0] !== 'function') {
        return (handleSubmit as any)(...args)
      }
      return (form.handleSubmit as any)(...args)
    }) as DynamicFormChildrenProps['handleSubmit']

    const imperativeApi = useMemo(
      () => ({
        ...form,
        reset: form.reset,
        handleSubmit,
        watch: form.watch,
        setValue: form.setValue,
        getValues: form.getValues,
        setError: form.setError,
        control: form.control,
      }),
      [form, handleSubmit],
    )

    useImperativeHandle(ref, () => imperativeApi, [imperativeApi])

    useEffect(() => {
      if (formInstance) {
        formInstance.current = imperativeApi
      }
    }, [formInstance, imperativeApi])

    useEffect(() => {
      if (
        process.env.NODE_ENV === 'development' &&
        Object.keys(form.formState.errors).length > 0
      ) {
        console.info('form.formState.errors: ', form.formState.errors)
      }
    }, [form.formState.errors])

    const renderField = (field: Field) => {
      if (field.type === 'fields-array') {
        return (
          <DynamicFormFieldArray
            key={field.name}
            field={field}
            form={form}
            variant={variant}
          />
        )
      }
      if (field.type === 'title') {
        return (
          <h3 className='flex col-span-12 text-base mt-6 gap-1' key={field.name}>
            {field.label} {field['aria-required'] && '*'}
          </h3>
        )
      }
      return (
        <DynamicFormFieldRender
          key={field.name}
          field={field}
          form={form}
          variant={variant}
          isScrollable={!!maxHeight}
        />
      )
    }

    const renderFieldsList = (list: Field[]) => {
      const visible = list.filter((field) => !field.hide).map((field) => renderField(field))
      const hidden = showOptions
        ? list.filter((field) => field.hide).map((field) => renderField(field))
        : []
      return [...visible, ...hidden]
    }

    const getFieldErrorCount = (field: Field) => {
      if (field.type === 'title') return 0
      return getValueAtPath(form.formState.errors, field.name) ? 1 : 0
    }

    const getSectionErrorCount = (section: DynamicFormSection) =>
      section.fields.reduce((count, field) => count + getFieldErrorCount(field), 0)

    const sectionEntries = (hasSections ? sections : [])
      .map((section) => ({
        section,
        content: renderFieldsList(section.fields),
        errorCount: getSectionErrorCount(section),
      }))
      .filter((entry) => entry.content.length > 0)

    const nonSectionContent = renderFieldsList(flatFields)

    const tabsDefaultValue =
      defaultSectionId ?? sectionEntries[0]?.section.id ?? undefined

    const sectionsContent =
      hasSections && sectionsVariant === 'tabs' ? (
        sectionEntries.length > 0 ? (
          <div className='col-span-12'>
            <Tabs.Root defaultValue={tabsDefaultValue} className='w-full'>
              <Tabs.List className='mb-2 flex w-full flex-wrap justify-start h-auto'>
                {sectionEntries.map(({ section, errorCount }) => (
                  <Tabs.Trigger
                    key={section.id}
                    value={section.id}
                    data-has-error={errorCount > 0 ? 'true' : undefined}
                  >
                    <span className='inline-flex items-center gap-2'>
                      {section.tabLabel ?? section.title ?? section.id}
                      {errorCount > 0 && (
                        <span
                          data-section-error-badge={section.id}
                          className='inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-100 px-1.5 text-xs font-medium text-red-700'
                          aria-label={`${errorCount} erreur${errorCount > 1 ? 's' : ''}`}
                        >
                          {errorCount}
                        </span>
                      )}
                    </span>
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              {sectionEntries.map(({ section, content }) => (
                <Tabs.Content key={section.id} value={section.id} className='mt-0'>
                  <div className='grid grid-cols-12 gap-4'>{content}</div>
                </Tabs.Content>
              ))}
            </Tabs.Root>
          </div>
        ) : null
      ) : (
        sectionEntries.flatMap(({ section, content }) => [
          section.title ? (
            <h3
              key={`${section.id}-title`}
              className='col-span-12 flex text-base mt-6 gap-1'
            >
              {section.title}
            </h3>
          ) : null,
          ...content,
        ])
      )

    const content = (
      <>
        {hasSections ? sectionsContent : nonSectionContent}
        <ToggleShowOptions
          showOptions={showOptions}
          setShowOptions={setShowOptions}
          fields={flatFields}
        />
      </>
    )

    return (
      <Form.Provider {...form}>
        <form
          id={id}
          method='post'
          onSubmit={handleSubmit}
          className={cx('grid w-full grid-cols-12 gap-4 px-1', className)}
        >
          {maxHeight ? (
            <ScrollArea
              className='col-span-12'
              style={{
                maxHeight:
                  typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight,
              }}
              contentCn='grid grid-cols-12 gap-4 px-1'
            >
              {content}
            </ScrollArea>
          ) : (
            content
          )}

          {children && (
            <div className='col-span-12 flex justify-end space-x-4'>
              {children({ handleSubmit: childHandleSubmit, form })}
            </div>
          )}
          {flatFields.some((field) => field['aria-required']) && (
            <div className='col-span-12 text-sm text-slate-500 italic'>
              * champs obligatoires
            </div>
          )}
          <button type='submit' className='hidden'>
            Submit
          </button>
        </form>
      </Form.Provider>
    )
  },
)

DynamicForm.displayName = 'DynamicForm'
export { DynamicForm }

type ToggleShowOptionsProps = {
  showOptions: boolean
  setShowOptions: React.Dispatch<React.SetStateAction<boolean>>
  fields: Field[]
}

const ToggleShowOptions = ({
  showOptions,
  setShowOptions,
  fields,
}: ToggleShowOptionsProps) => {
  const toggleVisibility = () => setShowOptions((prev) => !prev)
  return (
    fields.some((field) => field.hide) && (
      <div className='col-span-12'>
        {showOptions ? (
          <Button type='button' variant='action' onClick={toggleVisibility}>
            Moins d&apos;options
          </Button>
        ) : (
          <Button type='button' variant='action' onClick={toggleVisibility}>
            Plus d&apos;options
          </Button>
        )}
      </div>
    )
  )
}

export const reactSelectValidation = z.object({
  label: z.string(),
  value: z.string(),
})
